import { useState, useEffect } from "react";
import {
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Avatar,
  Grid,
  InputAdornment,
  Stack,
  useMediaQuery,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import {
  Search,
  Trash2,
  Plus,
  Edit,
  Eye,
  ChevronLeft,
  ChevronRight,
  X as XIcon,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useSearchParams, useNavigate } from "react-router-dom";
import { getPets, deletePet, getUserProfile, updatePetBasicInfo } from "../services/api";
import Cookies from "js-cookie";
import PetInfoModal from "../components/PetInfoModal";
import { convertFromISO } from "../utils/dateValidation";
import dayjs from "dayjs";
import { useTranslation } from 'react-i18next';
import { set } from "lodash";
import { useNotifications } from '../contexts/NotificationContext';

// Helper function to convert date to YYYY/MM/DD format 
const formatToYYYYMMDD = (dateString) => {
  if (!dateString) return "";
  const date = dayjs(dateString);
  return date.isValid() ? date.format("YYYY/MM/DD") : dateString;
};

const PetManagementHome = () => {
  const userId = Cookies.get("id");

  // Pets data state
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [buttonLoading, setButtonLoading] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [apiTotal, setApiTotal] = useState(0); // Store total from API
  const petsPerPage = 10;

  // Filter and search state
  const [searchText, setSearchText] = useState("");
  const [filteredPets, setFilteredPets] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");

  // Sorting state
  const [sort, setSort] = useState({ key: null, direction: null });

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingPetId, setDeletingPetId] = useState(null);

  //tag embed section
  const [searchParams] = useSearchParams();
  const qrParam = searchParams.get("qr");
  const navigateFaceID = searchParams.get("faceID");
  const [askTagEmbed, setAskTagEmbed] = useState(!!qrParam);
  const [selectPet, setSelectPet] = useState(false);
  const [loadingPetId, setLoadingPetId] = useState(null);

  // Page input state
  const [isEditingPage, setIsEditingPage] = useState(false);
  const [pageInput, setPageInput] = useState(currentPage.toString());

  // Mobile and navigation
  const isMobile = useMediaQuery("(max-width:850px)");
  const navigate = useNavigate();

  const [selectedPet, setSelectedPet] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleViewPet = (pet) => {
    setSelectedPet(pet);
    setModalOpen(true);
  };

  const { t } = useTranslation();
  const { fetchNotifications } = useNotifications();

  useEffect(() => {
    const fetchUserProfile = async () => {
      const userProfile = await getUserProfile(userId);
      const userRole = userProfile.data.user.role;
      Cookies.set("userRole", userRole, { expires: 1 });
    };

    fetchUserProfile();
    // Fetch notifications concurrently when component mounts
    fetchNotifications(true);
  }, []);

  const faceIDCompleted = (registered) => {
    const color = registered ? "#FF8D1A" : "#969696";
    return (
      <div className="size-[14px]">
        <div className="relative size-full" data-name="face">
          <div
            className="absolute left-[calc(50%+0.01px)] size-[15px] top-[calc(50%+0.02px)] translate-x-[-50%] translate-y-[-50%]"
            data-name="Vector"
          >
            <div className="absolute inset-[-5.56%]">
              <svg
                className="block size-full"
                fill="none"
                preserveAspectRatio="none"
                viewBox="0 0 17 17"
              >
                <path
                  d="M5 0.833333H2.5C2.05797 0.833333 1.63405 1.00893 1.32149 1.32149C1.00893 1.63405 0.833333 2.05797 0.833333 2.5V5M15.8333 5V2.5C15.8333 2.05797 15.6577 1.63405 15.3452 1.32149C15.0326 1.00893 14.6087 0.833333 14.1667 0.833333H11.6667M11.6667 15.8333H14.1667C14.6087 15.8333 15.0326 15.6577 15.3452 15.3452C15.6577 15.0326 15.8333 14.6087 15.8333 14.1667V11.6667M0.833333 11.6667V14.1667C0.833333 14.6087 1.00893 15.0326 1.32149 15.3452C1.63405 15.6577 2.05797 15.8333 2.5 15.8333H5"
                  id="Vector"
                  stroke={color}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.66667"
                />
              </svg>
            </div>
          </div>

          <div className="absolute content-stretch flex flex-col gap-[1.102px] items-center left-[calc(50%-0.08px)] top-[calc(50%+1.04px)] translate-x-[-50%] translate-y-[-50%] w-[9.848px]">
            <div className="content-stretch flex items-center justify-between relative shrink-0 w-full">
              <div className="relative shrink-0 size-[2.41px]">
                <svg
                  className="block size-full"
                  fill="none"
                  preserveAspectRatio="none"
                  viewBox="0 0 3 3"
                >
                  <circle
                    cx="1.20523"
                    cy="1.20523"
                    fill={color}
                    id="Ellipse 7151"
                    r="1.20523"
                  />
                </svg>
              </div>
              <div className="relative shrink-0 size-[2.41px]">
                <svg
                  className="block size-full"
                  fill="none"
                  preserveAspectRatio="none"
                  viewBox="0 0 3 3"
                >
                  <circle
                    cx="1.20523"
                    cy="1.20523"
                    fill={color}
                    id="Ellipse 7151"
                    r="1.20523"
                  />
                </svg>
              </div>
            </div>

            <div className="h-[4.167px] relative shrink-0 w-[5px]">
              <div className="absolute bottom-[-10%] left-[-8.33%] right-[-8.33%] top-0">
                <svg
                  className="block size-full"
                  fill="none"
                  preserveAspectRatio="none"
                  viewBox="0 0 6 5"
                >
                  <g id="Frame 2085662674">
                    <path
                      d="M3.34516 2.45265C3.1142 2.70267 2.71913 2.70267 2.48818 2.45265L1.80045 1.70816C1.45532 1.33454 1.72031 0.729004 2.22894 0.729004L3.6044 0.729004C4.11303 0.729004 4.37802 1.33455 4.03289 1.70816L3.34516 2.45265Z"
                      fill={color}
                      id="Polygon 5"
                    />
                    <g id="Frame 2085662673">
                      <path
                        d="M0.833333 2.9165C0.833333 2.68639 0.646785 2.49984 0.416667 2.49984C0.186548 2.49984 0 2.68639 0 2.9165H0.416667H0.833333ZM2.91667 2.9165H2.5C2.5 3.37674 2.1269 3.74984 1.66667 3.74984V4.1665V4.58317C2.58714 4.58317 3.33333 3.83698 3.33333 2.9165H2.91667ZM1.66667 4.1665V3.74984C1.20643 3.74984 0.833333 3.37674 0.833333 2.9165H0.416667H0C0 3.83698 0.746192 4.58317 1.66667 4.58317V4.1665Z"
                        fill={color}
                        id="Ellipse 7167"
                      />
                      <path
                        d="M5 2.9165C5 2.68639 5.18655 2.49984 5.41667 2.49984C5.64679 2.49984 5.83334 2.68639 5.83334 2.9165H5.41667H5ZM2.91667 2.9165H3.33334C3.33334 3.37674 3.70643 3.74984 4.16667 3.74984V4.1665V4.58317C3.24619 4.58317 2.5 3.83698 2.5 2.9165H2.91667ZM4.16667 4.1665V3.74984C4.62691 3.74984 5 3.37674 5 2.9165H5.41667H5.83334C5.83334 3.83698 5.08714 4.58317 4.16667 4.58317V4.1665Z"
                        fill={color}
                        id="Ellipse 7168"
                      />
                    </g>
                  </g>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  };

  // Load pets data
  useEffect(() => {
    const loadPets = async () => {
      try {
        setLoading(true);
        const response = await getPets(userId, currentPage);
        const petsData = response.data.form || [];
        
        // Set pets with empty arrays for medical-related data (will be fetched in PetInfoModal)
        const petsWithEmptyMedicalData = petsData.map((pet) => ({
          ...pet,
          medical: [],
          medication: [],
          vaccineRecords: [],
          deworm: [],
          bloodTest: []
        }));

        setPets(petsWithEmptyMedicalData);
        setFilteredPets(petsWithEmptyMedicalData);
        const totalPets = response.data.total;
        setApiTotal(totalPets);
        setTotalResults(totalPets);
        setTotalPages(Math.max(1, Math.ceil(totalPets / petsPerPage)));
        if (navigateFaceID && petsWithEmptyMedicalData.length > 0) {
          navigate(`/pet-management/edit/${navigateFaceID}/faceID`, { state: { petData: petsWithEmptyMedicalData.find(pet => pet._id === navigateFaceID) } })
        }
      } catch (err) {
        if (err.response && err.response.status === 404) {
          console.log("No pets found for this user.");
        }
        console.error("Error loading pets:", err);
        <Typography variant="h6" color="text.secondary">
          {t("NoPets")}
        </Typography>
      } finally {
        setLoading(false);
      }
    };

    loadPets();
  }, [currentPage]);

  // Reset page to 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, statusFilter]);

  // Filter pets when search text, status filter, or pets data changes
  useEffect(() => {
    let filtered = pets;
    const hasActiveFilters = searchText.trim() !== "" || statusFilter !== "all";

    // Apply search filter
    if (searchText.trim() !== "") {
      filtered = filtered.filter(
        (pet) =>
          (pet.name &&
            pet.name.toLowerCase().includes(searchText.toLowerCase())) ||
          (pet.sex &&
            pet.sex.toLowerCase().includes(searchText.toLowerCase())) ||
          (pet.status &&
            pet.status.toLowerCase().includes(searchText.toLowerCase()))
      );
    }

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((pet) => pet.status === statusFilter);
    }

    setFilteredPets(filtered);
    // Only use filtered length if filters are active, otherwise use API total
    if (hasActiveFilters) {
      setTotalResults(filtered.length);
      setTotalPages(Math.max(1, Math.ceil(filtered.length / petsPerPage)));
    } else {
      setTotalResults(apiTotal);
      setTotalPages(Math.max(1, Math.ceil(apiTotal / petsPerPage)));
    }
  }, [searchText, statusFilter, pets, apiTotal]);

  const sortFns = {
    birthday: (a, b) => new Date(a.birthday) - new Date(b.birthday),
    dewormDate: (a, b) => new Date(a.dewormDate) - new Date(b.dewormDate),
    vaccineDate: (a, b) => new Date(a.vaccineDate) - new Date(b.vaccineDate),
  };

  // Sorting
  let sortedPets = [...filteredPets];
  if (sort.key && sort.direction) {
    sortedPets.sort((a, b) => {
      const cmp = sortFns[sort.key](a, b);
      return sort.direction === "asc" ? cmp : -cmp;
    });
  }

  // Pagination - API handles pagination, so display all pets returned
  const currentPets = sortedPets;

  const handleDelete = async (petId) => {
    setButtonLoading(true);
    await deletePet(petId);
    setPets((prev) => prev.filter((pet) => pet._id !== petId));
    setButtonLoading(false);
    setShowDeleteModal(false);
  };

  const handleAddPet = () => {
    navigate("/pet-management/add");
  };

  const handleEdit = (pet) => {
    navigate(`/pet-management/edit/${pet._id}`, { state: { petData: pet } });
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const clearSearch = () => {
    setSearchText("");
    setStatusFilter("all");
  };

  // Sorting handler
  const handleSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, direction: "asc" };
      if (prev.direction === "asc") return { key, direction: "desc" };
      return { key: null, direction: null };
    });
  };

  // Page input handlers
  const handlePageClick = () => {
    setIsEditingPage(true);
    setPageInput(currentPage.toString());
  };

  const handlePageInputChange = (e) => {
    const value = e.target.value;
    // Only allow numbers
    if (/^\d*$/.test(value)) {
      setPageInput(value);
    }
  };

  const handlePageInputSubmit = () => {
    const pageNum = parseInt(pageInput);
    if (pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
    } else {
      setPageInput(currentPage.toString());
    }
    setIsEditingPage(false);
  };

  const handlePageInputKeyPress = (e) => {
    if (e.key === "Enter") {
      handlePageInputSubmit();
    } else if (e.key === "Escape") {
      setPageInput(currentPage.toString());
      setIsEditingPage(false);
    }
  };

  // Update pageInput when currentPage changes
  useEffect(() => {
    setPageInput(currentPage.toString());
  }, [currentPage]);

  // const getLatestVaccineDate = (vaccine) => {
  //   if (!vaccine || vaccine.length === 0) return null;
  //   const sortedDates = vaccine
  //     .map((record) => record.vaccineDate)
  //     .filter((date) => date)
  //     .sort(
  //       (a, b) =>
  //         new Date(b.split("/").reverse().join("-")) -
  //         new Date(a.split("/").reverse().join("-"))
  //     );
  //   return sortedDates.length > 0 ? convertFromISO(sortedDates[0]) : null;
  // };

  const getNextDewormingDate = (deworm) => {
    // if (!deworm || deworm.length === 0) return null;
    if (!Array.isArray(deworm) || deworm.length === 0) {
      return null;
    }
    // Find the most recent deworm record
    const sortedRecords = deworm
      .filter((record) => record.nextDewormDate)
      .sort(
        (a, b) =>
          new Date(b.date.split("/").reverse().join("-")) -
          new Date(a.date.split("/").reverse().join("-"))
      );

    if (sortedRecords.length === 0) return null;

    const latestRecord = sortedRecords[0];
    const latestDate = new Date(
      latestRecord.date.split("/").reverse().join("-")
    );

    // If the record has a nextDewormDate, use it
    if (latestRecord.nextDewormDate) {
      return convertFromISO(latestRecord.nextDewormDate);
    }

    // Otherwise, calculate next date based on frequency (default 3 months)
    const frequency = latestRecord.frequency || "3個月";
    const monthsToAdd = frequency.includes("1個月")
      ? 1
      : frequency.includes("2個月")
        ? 2
        : frequency.includes("3個月")
          ? 3
          : frequency.includes("6個月")
            ? 6
            : 3;

    const nextDate = new Date(latestDate);
    nextDate.setMonth(nextDate.getMonth() + monthsToAdd);

    return nextDate
      .toLocaleDateString("zh-TW", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
      .split("/")
      .join("/");
  };

  const getLatestVaccineDate = (vaccine) => {
    if (!vaccine || vaccine.length === 0) return null;
    const sortedDates = vaccine
      .map((record) => record.vaccineDate)
      .filter((date) => date)
      .sort(
        (a, b) =>
          new Date(b.split("/").reverse().join("-")) -
          new Date(a.split("/").reverse().join("-"))
      );
    return sortedDates.length > 0 ? convertFromISO(sortedDates[0]) : null;
  };

  // Mobile card layout
  const renderMobileList = () => (
    <Box>
      {currentPets.map((pet) => (
        <Box
          key={pet._id}
          sx={{
            background: "#fff",
            borderRadius: 3,
            p: 2,
            mb: 3,
            boxShadow: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>

            <Avatar
              src={pet?.breedimage?.[0]}
              alt={pet.name}
              sx={{ width: 56, height: 56, mr: 2 }}
            />
            <Box sx={{ flex: 1 }}>
              <Chip
                label={pet.status}
                color={
                  pet.status === "遺失"
                    ? "error"
                    : pet.status === "在家"
                      ? "success"
                      : "info"
                }
                size="small"
                sx={{
                  fontWeight: 700,
                  fontSize: 14,
                  px: 1.5,
                  background:
                    pet.status === "遺失"
                      ? "#FFEAEA"
                      : pet.status === "在家"
                        ? "#EAF6FF"
                        : "transparent",
                  color:
                    pet.status === "遺失"
                      ? "#F44336"
                      : pet.status === "在家"
                        ? "#2196F3"
                        : "#000",
                }}
                icon={
                  pet.status === "遺失" ? (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        bgcolor: "#F44336",
                        borderRadius: "50%",
                      }}
                    />
                  ) : pet.status === "在家" ? (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        bgcolor: "#2196F3",
                        borderRadius: "50%",
                      }}
                    />
                  ) : (
                    <Box />
                  )
                }
              />
              <Box fontWeight={700} fontSize={18} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {pet.sex === "male"
                  ?
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 1V5C16 5.28333 15.904 5.521 15.712 5.713C15.52 5.905 15.2827 6.00067 15 6C14.7173 5.99933 14.48 5.90333 14.288 5.712C14.096 5.52067 14 5.28333 14 5V3.425L10.025 7.375C10.3417 7.84167 10.5833 8.33767 10.75 8.863C10.9167 9.38833 11 9.934 11 10.5C11 12.0333 10.4667 13.3333 9.4 14.4C8.33333 15.4667 7.03333 16 5.5 16C3.96667 16 2.66667 15.4667 1.6 14.4C0.533333 13.3333 0 12.0333 0 10.5C0 8.96667 0.533333 7.66667 1.6 6.6C2.66667 5.53333 3.96667 5 5.5 5C6.05 5 6.59167 5.079 7.125 5.237C7.65833 5.395 8.15 5.641 8.6 5.975L12.575 2H11C10.7167 2 10.4793 1.904 10.288 1.712C10.0967 1.52 10.0007 1.28267 10 1C9.99933 0.717333 10.0953 0.48 10.288 0.288C10.4807 0.0960001 10.718 0 11 0H15C15.2833 0 15.521 0.0960001 15.713 0.288C15.905 0.48 16.0007 0.717333 16 1ZM5.5 7C4.53333 7 3.70833 7.34167 3.025 8.025C2.34167 8.70833 2 9.53333 2 10.5C2 11.4667 2.34167 12.2917 3.025 12.975C3.70833 13.6583 4.53333 14 5.5 14C6.46667 14 7.29167 13.6583 7.975 12.975C8.65833 12.2917 9 11.4667 9 10.5C9 9.53333 8.65833 8.70833 7.975 8.025C7.29167 7.34167 6.46667 7 5.5 7Z" fill="black" />
                  </svg>
                  : pet.sex === "female"
                    ?
                    <svg width="11" height="17" viewBox="0 0 11 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4.5 15H3.5C3.21667 15 2.97933 14.904 2.788 14.712C2.59667 14.52 2.50067 14.2827 2.5 14C2.49933 13.7173 2.59533 13.48 2.788 13.288C2.98067 13.096 3.218 13 3.5 13H4.5V10.9C3.18333 10.6667 2.104 10.0373 1.262 9.012C0.420001 7.98667 -0.000665876 6.79933 7.91139e-07 5.45C7.91139e-07 3.93333 0.537667 2.646 1.613 1.588C2.68833 0.53 3.984 0.000667296 5.5 6.28931e-07C7.016 -0.000666038 8.312 0.528667 9.388 1.588C10.464 2.64733 11.0013 3.93467 11 5.45C11 6.8 10.579 7.98767 9.737 9.013C8.895 10.0383 7.816 10.6673 6.5 10.9V13H7.5C7.78333 13 8.021 13.096 8.213 13.288C8.405 13.48 8.50067 13.7173 8.5 14C8.49933 14.2827 8.40333 14.5203 8.212 14.713C8.02067 14.9057 7.78333 15.0013 7.5 15H6.5V16C6.5 16.2833 6.404 16.521 6.212 16.713C6.02 16.905 5.78267 17.0007 5.5 17C5.21733 16.9993 4.98 16.9033 4.788 16.712C4.596 16.5207 4.5 16.2833 4.5 16V15ZM5.5 9C6.46667 9 7.29167 8.65833 7.975 7.975C8.65833 7.29167 9 6.46667 9 5.5C9 4.53333 8.65833 3.70833 7.975 3.025C7.29167 2.34167 6.46667 2 5.5 2C4.53333 2 3.70833 2.34167 3.025 3.025C2.34167 3.70833 2 4.53333 2 5.5C2 6.46667 2.34167 7.29167 3.025 7.975C3.70833 8.65833 4.53333 9 5.5 9Z" fill="black" />
                    </svg>

                    : pet.sex}

                {pet.name}
              </Box>
              <Box fontSize={14} color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {convertFromISO(pet.birthday)} {faceIDCompleted(pet.isRegistered)}
              </Box>
            </Box>
            <Stack direction="row" spacing={1}>
              <IconButton onClick={() => handleViewPet(pet)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </IconButton>
              <IconButton color="primary" onClick={() => handleEdit(pet)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="#FFB60C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M18.5 2.49632C18.8978 2.0985 19.4374 1.875 20 1.875C20.5626 1.875 21.1022 2.0985 21.5 2.49632C21.8978 2.89415 22.1213 3.43371 22.1213 3.99632C22.1213 4.55893 21.8978 5.0985 21.5 5.49632L12 14.9963L8 15.9963L9 11.9963L18.5 2.49632Z" stroke="#FFB60C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </IconButton>
              <IconButton
                color="error"
                onClick={() => {
                  setShowDeleteModal(true);
                  setDeletingPetId(pet._id);
                }}
              >
                <svg width="22" height="22" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18.418 6.4974H23.8346V8.66406H21.668V22.7474C21.668 23.3457 21.183 23.8307 20.5846 23.8307H5.41797C4.81967 23.8307 4.33464 23.3457 4.33464 22.7474V8.66406H2.16797V6.4974H7.58463V3.2474C7.58463 2.64909 8.06966 2.16406 8.66797 2.16406H17.3346C17.933 2.16406 18.418 2.64909 18.418 3.2474V6.4974ZM19.5013 8.66406H6.5013V21.6641H19.5013V8.66406ZM9.7513 11.9141H11.918V18.4141H9.7513V11.9141ZM14.0846 11.9141H16.2513V18.4141H14.0846V11.9141ZM9.7513 4.33073V6.4974H16.2513V4.33073H9.7513Z" fill="#969696" />
                </svg>
              </IconButton>
            </Stack>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Grid container spacing={4}>
            <Grid item xs={6}>
              <Typography fontWeight={700} fontSize={14}>
                {t("PetID")}
              </Typography>
              <Typography fontSize={14}>
                {pet.tagId || t('No')}
              </Typography>
              <Typography fontWeight={700} fontSize={14} mt={1}>
                {t("Neutered")}
              </Typography>
              <Typography fontSize={14}>
                {pet.sterilization ? t('Yes') : t('No')}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography fontWeight={700} fontSize={14}>
                {t("Medical")}
              </Typography>
              <Typography fontSize={14}>
                {pet.medicalRecordsCount > 0 ? t('Yes') : t('No')}
              </Typography>
              <Typography fontWeight={700} fontSize={14} mt={1}>
                {t("Medication")}
              </Typography>
              <Typography fontSize={14}>
                {pet.medicationRecordsCount > 0 ? t('Yes') : t('No')}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography fontWeight={700} fontSize={14}>
                {t("RecentDewormingRecords")}
              </Typography>
              <Typography fontSize={14}>
                {pet.dewormRecordsCount > 0 ? formatToYYYYMMDD(pet.latestDewormDate) : t('No')} 
              </Typography>
              <Typography fontWeight={700} fontSize={14} mt={1}>
                {t("RecentVaccineRecords")}
              </Typography>
              <Typography fontSize={14}>
                {pet.vaccineRecordsCount > 0 ? formatToYYYYMMDD(pet.latestVaccineDate) : t('No')}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      ))}
    </Box>
  );

  // Desktop table layout
  const renderDesktopTable = () => (
    <TableContainer
      component={Paper}
      sx={{ borderRadius: 3, boxShadow: "none" }}
    >
      <Table>
        <TableHead>
          <TableRow sx={{ background: "#F8F8F8" }}>
            <TableCell sx={{ fontWeight: 700, paddingLeft: "60px" }}>{t("Name")}</TableCell>
            <TableCell sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>{t("FaceId")} {faceIDCompleted(false)}</TableCell>
            <TableCell
              sx={{ fontWeight: 700, cursor: "pointer" }}
              onClick={() => handleSort("birthday")}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {t("DateofBirth")}
                {sort.key === "birthday" &&
                  (sort.direction === "asc" ? (
                    <ArrowUp size={16} />
                  ) : (
                    <ArrowDown size={16} />
                  ))}
              </Box>
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }}>{t("Status")}</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>{t("PetID")}</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>{t("Neutered")}</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>{t("Medical")}</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>{t("Medication")}</TableCell>
            <TableCell
              sx={{ fontWeight: 700, cursor: "pointer" }}
              onClick={() => handleSort("dewormDate")}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {t("RecentDewormingRecords")}
                {sort.key === "dewormDate" &&
                  (sort.direction === "asc" ? (
                    <ArrowUp size={16} />
                  ) : (
                    <ArrowDown size={16} />
                  ))}
              </Box>
            </TableCell>
            <TableCell
              sx={{ fontWeight: 700, cursor: "pointer" }}
              onClick={() => handleSort("vaccineDate")}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {t("RecentVaccineRecords")}
                {sort.key === "vaccineDate" &&
                  (sort.direction === "asc" ? (
                    <ArrowUp size={16} />
                  ) : (
                    <ArrowDown size={16} />
                  ))}
              </Box>
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {currentPets.map((pet) => (
            <TableRow key={pet._id}>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Avatar
                    src={pet?.breedimage?.[0]}
                    alt={pet.name}
                    sx={{ width: 36, height: 36, }}
                  />
                  {pet.sex === "male"
                    ?
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M16 1V5C16 5.28333 15.904 5.521 15.712 5.713C15.52 5.905 15.2827 6.00067 15 6C14.7173 5.99933 14.48 5.90333 14.288 5.712C14.096 5.52067 14 5.28333 14 5V3.425L10.025 7.375C10.3417 7.84167 10.5833 8.33767 10.75 8.863C10.9167 9.38833 11 9.934 11 10.5C11 12.0333 10.4667 13.3333 9.4 14.4C8.33333 15.4667 7.03333 16 5.5 16C3.96667 16 2.66667 15.4667 1.6 14.4C0.533333 13.3333 0 12.0333 0 10.5C0 8.96667 0.533333 7.66667 1.6 6.6C2.66667 5.53333 3.96667 5 5.5 5C6.05 5 6.59167 5.079 7.125 5.237C7.65833 5.395 8.15 5.641 8.6 5.975L12.575 2H11C10.7167 2 10.4793 1.904 10.288 1.712C10.0967 1.52 10.0007 1.28267 10 1C9.99933 0.717333 10.0953 0.48 10.288 0.288C10.4807 0.0960001 10.718 0 11 0H15C15.2833 0 15.521 0.0960001 15.713 0.288C15.905 0.48 16.0007 0.717333 16 1ZM5.5 7C4.53333 7 3.70833 7.34167 3.025 8.025C2.34167 8.70833 2 9.53333 2 10.5C2 11.4667 2.34167 12.2917 3.025 12.975C3.70833 13.6583 4.53333 14 5.5 14C6.46667 14 7.29167 13.6583 7.975 12.975C8.65833 12.2917 9 11.4667 9 10.5C9 9.53333 8.65833 8.70833 7.975 8.025C7.29167 7.34167 6.46667 7 5.5 7Z" fill="black" />
                    </svg>
                    : pet.sex === "female"
                      ?
                      <svg width="11" height="17" viewBox="0 0 11 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4.5 15H3.5C3.21667 15 2.97933 14.904 2.788 14.712C2.59667 14.52 2.50067 14.2827 2.5 14C2.49933 13.7173 2.59533 13.48 2.788 13.288C2.98067 13.096 3.218 13 3.5 13H4.5V10.9C3.18333 10.6667 2.104 10.0373 1.262 9.012C0.420001 7.98667 -0.000665876 6.79933 7.91139e-07 5.45C7.91139e-07 3.93333 0.537667 2.646 1.613 1.588C2.68833 0.53 3.984 0.000667296 5.5 6.28931e-07C7.016 -0.000666038 8.312 0.528667 9.388 1.588C10.464 2.64733 11.0013 3.93467 11 5.45C11 6.8 10.579 7.98767 9.737 9.013C8.895 10.0383 7.816 10.6673 6.5 10.9V13H7.5C7.78333 13 8.021 13.096 8.213 13.288C8.405 13.48 8.50067 13.7173 8.5 14C8.49933 14.2827 8.40333 14.5203 8.212 14.713C8.02067 14.9057 7.78333 15.0013 7.5 15H6.5V16C6.5 16.2833 6.404 16.521 6.212 16.713C6.02 16.905 5.78267 17.0007 5.5 17C5.21733 16.9993 4.98 16.9033 4.788 16.712C4.596 16.5207 4.5 16.2833 4.5 16V15ZM5.5 9C6.46667 9 7.29167 8.65833 7.975 7.975C8.65833 7.29167 9 6.46667 9 5.5C9 4.53333 8.65833 3.70833 7.975 3.025C7.29167 2.34167 6.46667 2 5.5 2C4.53333 2 3.70833 2.34167 3.025 3.025C2.34167 3.70833 2 4.53333 2 5.5C2 6.46667 2.34167 7.29167 3.025 7.975C3.70833 8.65833 4.53333 9 5.5 9Z" fill="black" />
                      </svg>

                      : pet.sex}

                  <Typography fontWeight={500}>{pet.name}</Typography>
                </Box>
              </TableCell>
              <TableCell>{pet.isRegistered ? t('Registered') : t('No')}</TableCell>
              <TableCell>{convertFromISO(pet.birthday)}</TableCell>
              <TableCell>
                <Chip
                  label={
                    pet.status === "遺失"
                      ? t("Lost")
                      : pet.status === "在家"
                        ? t("AtHome")
                        : pet.status
                  }
                  color={pet.status === "遺失" ? "error" : "info"}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: 14,
                    px: 1.5,
                    background:
                      pet.status === "遺失"
                        ? "#FFEAEA"
                        : pet.status === "在家"
                          ? "#EAF6FF"
                          : "transparent",
                    color:
                      pet.status === "遺失"
                        ? "#F44336"
                        : pet.status === "在家"
                          ? "#2196F3"
                          : "#000",
                  }}
                  icon={
                    pet.status === "遺失" ? (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          bgcolor: "#F44336",
                          borderRadius: "50%",
                        }}
                      />
                    ) : pet.status === "在家" ? (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          bgcolor: "#2196F3",
                          borderRadius: "50%",
                        }}
                      />
                    ) : (
                      <Box />
                    )
                  }
                />
              </TableCell>
              <TableCell>{pet.tagId}</TableCell>
              <TableCell>{pet.sterilization === true ? t('Yes') : t('No')}</TableCell>
              <TableCell>{pet.medicalRecordsCount > 0 ? t('Yes') : t('No')}</TableCell>
              <TableCell>{pet.medicationRecordsCount > 0 ? t('Yes') : t('No')}</TableCell>
              <TableCell>
                {pet.dewormRecordsCount > 0 ? formatToYYYYMMDD(pet.latestDewormDate) : t('No')}
              </TableCell>
              <TableCell>
                {pet.vaccineRecordsCount > 0 ? formatToYYYYMMDD(pet.latestVaccineDate) : t('No')}
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={1}>
                  <IconButton
                    color="default"
                    onClick={() => handleViewPet(pet)}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </IconButton>
                  <IconButton color="primary" onClick={() => handleEdit(pet)}>

                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="#FFB60C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M18.5 2.49632C18.8978 2.0985 19.4374 1.875 20 1.875C20.5626 1.875 21.1022 2.0985 21.5 2.49632C21.8978 2.89415 22.1213 3.43371 22.1213 3.99632C22.1213 4.55893 21.8978 5.0985 21.5 5.49632L12 14.9963L8 15.9963L9 11.9963L18.5 2.49632Z" stroke="#FFB60C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>

                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => {
                      setShowDeleteModal(true);
                      setDeletingPetId(pet._id);
                    }}
                  >

                    <svg width="22" height="22" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18.418 6.4974H23.8346V8.66406H21.668V22.7474C21.668 23.3457 21.183 23.8307 20.5846 23.8307H5.41797C4.81967 23.8307 4.33464 23.3457 4.33464 22.7474V8.66406H2.16797V6.4974H7.58463V3.2474C7.58463 2.64909 8.06966 2.16406 8.66797 2.16406H17.3346C17.933 2.16406 18.418 2.64909 18.418 3.2474V6.4974ZM19.5013 8.66406H6.5013V21.6641H19.5013V8.66406ZM9.7513 11.9141H11.918V18.4141H9.7513V11.9141ZM14.0846 11.9141H16.2513V18.4141H14.0846V11.9141ZM9.7513 4.33073V6.4974H16.2513V4.33073H9.7513Z" fill="#969696" />
                    </svg>

                  </IconButton>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ backgroundColor: "#FCF8F3", minHeight: "100vh" }}>
      <Navbar />
      <Box
        sx={{
          maxWidth: isMobile ? 1200 : 1440,
          mx: "auto",
          pt: isMobile ? 12 : 6,
          pb: isMobile ? 6 : 12,
          px: 2,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "flex-start" : "center",
            justifyContent: isMobile ? "flex-start" : "space-between",
            mb: 2,
            mt: isMobile ? 0 : 12,
            gap: isMobile ? 2 : 0,

          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: isMobile ? 2 : 10,
              width: isMobile ? "100%" : "auto",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "flex-start" : "center",
            }}
          >
            <Typography variant={isMobile ? "h5" : "h3"} fontWeight={700}>
              {t("PetManagementTitle")}
            </Typography>
            {/* Search */}
            {/* <TextField
              size="small"
              placeholder="請輸入關鍵字"
              value={searchText}
              onChange={handleSearchChange}
              sx={{
                width: 260,
                background: "#fff",
                mt: isMobile ? 2 : 0,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {searchText && (
                      <IconButton size="small" onClick={clearSearch}>
                        <XIcon size={16} />
                      </IconButton>
                    )}
                    <Search size={18} />
                  </InputAdornment>
                ),
              }}
            /> */}
            {/* Status Filter */}
            {/* <FormControl
              size="small"
              sx={{
                width: 120,
                background: "#fff",
                mt: isMobile ? 2 : 0,
              }}
            >
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                displayEmpty
                sx={{
                  "& .MuiSelect-select": {
                    fontSize: 14,
                  },
                }}
              >
                <MenuItem value="all">全部狀態</MenuItem>
                <MenuItem value="在家">在家</MenuItem>
                <MenuItem value="遺失">遺失</MenuItem>
              </Select>
            </FormControl> */}
          </Box>
          {/* Add pet button */}
          <Button
            variant="outlined"
            color="black"
            onClick={handleAddPet}
            sx={{
              borderRadius: 2,
              fontWeight: 700,
              px: 3,
              py: 1,
              fontSize: 16,
              width: "auto",
              mt: isMobile ? 2 : 0,
              "&:hover": {
                backgroundColor: "black",
                color: "white",
              },
            }}
            startIcon={<Plus size={20} />}
          >
            {t('AddPets')}
          </Button>
        </Box>
        {/* Pagination */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            ml: isMobile ? 2 : 0,
            mb: 4,
            mt: 4,
            justifyContent: "space-between",
          }}
        >
          <Typography color="text.secondary">
            {10 * (currentPage - 1) + 1} - {10 * (currentPage - 1) + currentPets.length} / {totalResults} {t('results')}
          </Typography>
          {!loading && !error && totalPages > 1 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1,
                mr: isMobile ? 2 : 0,
              }}
            >
              <IconButton
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
                sx={{
                  border: "1px solid #E0E0E0",
                  borderRadius: 2,
                  width: 36,
                  height: 36,
                  "&:hover": {
                    backgroundColor: "#F5F5F5",
                  },
                  "&:disabled": {
                    borderColor: "#F0F0F0",
                    color: "#BDBDBD",
                  },
                }}
              >
                <ChevronLeft size={16} />
              </IconButton>

              {isEditingPage ? (
                <TextField
                  value={pageInput}
                  onChange={handlePageInputChange}
                  onKeyDown={handlePageInputKeyPress}
                  onBlur={handlePageInputSubmit}
                  size="small"
                  sx={{
                    mx: 2,
                    width: 60,
                    "& .MuiInputBase-root": {
                      fontSize: 14,
                      height: 32,
                    },
                    "& .MuiInputBase-input": {
                      textAlign: "center",
                      padding: "4px 8px",
                    },
                  }}
                  autoFocus
                  inputProps={{
                    min: 1,
                    max: totalPages,
                  }}
                />
              ) : (
                <Typography
                  onClick={handlePageClick}
                  sx={{
                    mx: 2,
                    fontSize: 14,
                    color: "#666666",
                    fontWeight: 400,
                    cursor: "pointer",
                    padding: "4px 8px",
                    borderRadius: 1,
                    "&:hover": {
                      backgroundColor: "#F5F5F5",
                    },
                  }}
                >
                  {currentPage}
                </Typography>
              )}
              <Typography
                sx={{
                  fontSize: 14,
                  color: "#666666",
                  fontWeight: 400,
                }}
              >
                {currentPage} / {totalPages}頁
              </Typography>

              <IconButton
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
                sx={{
                  border: "1px solid #E0E0E0",
                  borderRadius: 2,
                  width: 36,
                  height: 36,
                  "&:hover": {
                    backgroundColor: "#F5F5F5",
                  },
                  "&:disabled": {
                    borderColor: "#F0F0F0",
                    color: "#BDBDBD",
                  },
                }}
              >
                <ChevronRight size={16} />
              </IconButton>
            </Box>
          )}
        </Box>
        {/* Table or Mobile List */}
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box>
              {/* 載入中... */}
              <img src="https://petpetclub.s3.ap-southeast-1.amazonaws.com/user-uploads/pets/68cb893a0feda12f2dbcc02b/68d4c8ce22baedfbe8b852dd.gif" alt=""
                style={{
                  width: '150px',
                  height: 'auto',
                  mixBlendMode: 'multiply',
                }} />
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{
                  fontWeight: 500,
                  fontSize: "15px",
                  textAlign: "center",
                  fontFamily: "Lucida Console",
                }}
              >
                {t('Loading')}
              </Typography>
            </Box>
          </Box>
        ) : error ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 8,
            }}
          >
            <Typography variant="h6" color="error">
              {error}
            </Typography>
          </Box>
        ) : currentPets.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 8,
            }}
          >
            <Typography variant="h6" color="text.secondary">
              {t('NoPets')}
            </Typography>
          </Box>
        ) : isMobile ? (
          renderMobileList()
        ) : (
          renderDesktopTable()
        )}
      </Box>
      <Footer />
      <PetInfoModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        pet={selectedPet}
      />
      <Dialog
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            maxWidth: 400,
            width: "100%",
            p: 3,
            m: 2,
            boxSizing: "border-box",
            textAlign: "center",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            fontWeight: 700,
            fontSize: 20,
            justifyContent: "center",
            gap: 1,
          }}
        >

          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10.2947 3.86118L1.82466 18.0012C1.65003 18.3036 1.55763 18.6465 1.55665 18.9957C1.55567 19.3449 1.64615 19.6883 1.81909 19.9917C1.99202 20.2951 2.24139 20.5479 2.54237 20.725C2.84335 20.9021 3.18546 20.9973 3.53466 21.0012H20.4747C20.8239 20.9973 21.166 20.9021 21.467 20.725C21.7679 20.5479 22.0173 20.2951 22.1902 19.9917C22.3632 19.6883 22.4536 19.3449 22.4527 18.9957C22.4517 18.6465 22.3593 18.3036 22.1847 18.0012L13.7147 3.86118C13.5364 3.56729 13.2854 3.3243 12.9859 3.15567C12.6863 2.98703 12.3484 2.89844 12.0047 2.89844C11.6609 2.89844 11.323 2.98703 11.0235 3.15567C10.7239 3.3243 10.4729 3.56729 10.2947 3.86118Z" stroke="#FF1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M12 9V13" stroke="#FF1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M12 17H12.008" stroke="#FF1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>

          {t('ConfirmDeletePet')}
        </Box>
        <DialogContent sx={{ pt: 0, pb: 0 }}>
          <Typography
            variant="body1"
            sx={{ mb: 3, textAlign: "center", fontSize: 16 }}
          >
            {t('Systemwilldelete')}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            flexDirection: "column",
            "& > :not(style) ~ :not(style)": {
              marginLeft: 0,
            },
          }}
        >
          <Button
            fullWidth
            variant="contained"
            sx={{
              backgroundColor: "#FF1A1A",
              color: "#050505",
              fontWeight: 700,
              fontSize: 16,
              borderRadius: 2,
              py: 1,
              mb: 1,
              textAlign: "center",
              textTransform: 'none',
              "&:hover": { backgroundColor: "#d63e3eff" },
              cursor: buttonLoading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
            }}
            onClick={() => handleDelete(deletingPetId)}
          >
            {t('Delete')}
            {buttonLoading && (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <CircularProgress size={15} color="inherit" />
              </span>
            )}
          </Button>
          <Button
            fullWidth
            variant="text"
            sx={{
              color: "#B0B0B0",
              fontWeight: 700,
              fontSize: 16,
              borderRadius: 2,
              py: 1,
              textAlign: "center",
              textTransform: 'none',
            }}
            onClick={() => setShowDeleteModal(false)}
          >
            {t('cancel')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={askTagEmbed}
        onClose={() => setAskTagEmbed(false)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            maxWidth: 400,
            width: "100%",
            p: 3,
            m: 2,
            boxSizing: "border-box",
            textAlign: "center",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            fontWeight: 700,
            fontSize: 20,
            justifyContent: "flex-start",
            gap: 1,
          }}
        >
          <Button
            sx={{ color: "black", minWidth: 0, }}
            onClick={() => navigate("/landing")}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="20" viewBox="0 0 14 24" fill="none">
              <path d="M12 22L2 12L12 2" stroke="#050505" strokeWidth="3.33333" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Button>
          {t('EmbedTag')}
        </Box>
        <DialogActions
          sx={{
            flexDirection: "column",
            "& > :not(style) ~ :not(style)": {
              marginLeft: 0,
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <Button
              fullWidth
              variant="contained"
              sx={{
                fontWeight: 700,
                fontSize: 16,
                borderRadius: 2,
                py: 1,
                mb: 1,
                textAlign: "center",
                textTransform: 'none',
                color: "#969696",
                border: '1px solid #969696',
                backgroundColor: "white",
                "&:hover": { border: '1px solid #FFB60C', color: "#FFB60C" },
              }}
              onClick={() => { setSelectPet(true); setAskTagEmbed(false); }}
            >
              {t('ExistingPet')}
            </Button>
            <Button
              fullWidth
              variant="contained"
              sx={{
                color: "#969696",
                fontWeight: 700,
                fontSize: 16,
                borderRadius: 2,
                py: 1,
                mb: 1,
                textAlign: "center",
                textTransform: 'none',
                border: '1px solid #969696',
                backgroundColor: "white",
                "&:hover": { border: '1px solid #FFB60C', color: "#FFB60C" },
              }}
              onClick={() => navigate(`/pet-management/add?qr=${qrParam}`)}
            >
              {t('NewPet')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <Dialog
        open={selectPet}
        onClose={() => setSelectPet(false)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            maxWidth: 400,
            width: "100%",
            p: 3,
            m: 2,
            boxSizing: "border-box",
            textAlign: "center",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            fontWeight: 700,
            fontSize: 20,
            justifyContent: "flex-start",
            gap: 1,
          }}
        >
          <Button
            sx={{ color: "black", minWidth: 0, }}
            onClick={() => { setAskTagEmbed(true); setSelectPet(false); }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="20" viewBox="0 0 14 24" fill="none">
              <path d="M12 22L2 12L12 2" stroke="#050505" strokeWidth="3.33333" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Button>
          {t('EmbedTag')}
        </Box>
        <DialogActions
          sx={{
            flexDirection: "column",
            "& > :not(style) ~ :not(style)": {
              marginLeft: 0,
            },
          }}
        >
          {pets.length === 0 && (
            <Typography variant="h6" color="text.secondary">
              {t("NoPets")}
            </Typography>
          )}
          {pets.map((pet) => (
            <Button
              key={pet._id}
              fullWidth
              variant="contained"
              disabled={loadingPetId === pet._id}
              sx={{
                fontWeight: 700,
                fontSize: 16,
                borderRadius: 2,
                py: 1,
                mb: 1,
                textAlign: "center",
                textTransform: 'none',
                color: "#969696",
                border: '1px solid #969696',
                backgroundColor: "white",
                "&:hover": { border: '1px solid #FFB60C', color: "#FFB60C" },
                "&:disabled": { border: '1px solid #FFB60C', color: "#ffb60cff" },
                cursor: loadingPetId === pet._id ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
              }}
              onClick={async () => {
                try {
                  setLoadingPetId(pet._id);
                  // Update the pet's tagId with the qr parameter
                  await updatePetBasicInfo(pet._id, { ...pet, tagId: qrParam });
                  // Close modal and navigate
                  setSelectPet(false);
                  navigate("/pet-management", { replace: true });
                  // Reload pets to show updated data
                  const response = await getPets(userId, currentPage);
                  const petsData = response.data.form || [];
                  const totalPets = response.data.total;
                  setPets(petsData);
                  setFilteredPets(petsData);
                  setApiTotal(totalPets);
                  setTotalResults(totalPets);
                  setTotalPages(Math.max(1, Math.ceil(totalPets / petsPerPage)));
                } catch (error) {
                  console.error("Error updating pet tagId:", error);
                } finally {
                  setLoadingPetId(null);
                }
              }}
            >
              {pet.name}
              {loadingPetId === pet._id && (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <CircularProgress size={15} color="inherit" />
                </span>
              )}
            </Button>
          ))}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PetManagementHome;
